import { z } from 'zod';
import { label } from './text-policy.js';
import { pageQuerySchema } from './http.js';
import { kioskBrandingColorsSchema, kioskWelcomeCopySchema } from './kiosk-branding.js';
import { checkOutMatchModeSchema, hostLookupMinCharsSchema, hostLookupModeSchema } from './kiosk-setting.js';
/**
 * Lifecycle of a paired device (`docs/adr/0008`).
 *
 * Must stay identical — same members, **same order** — to the `DeviceStatus`
 * Prisma enum in packages/db/prisma/schema.prisma, proven by a lock-step test
 * in packages/db, exactly like `PERSON_TYPES` in ./person.ts. This package is
 * browser-safe and may never import the Prisma client to check for itself.
 */
export const DEVICE_STATUSES = ['PENDING', 'ACTIVE', 'REVOKED'];
export const deviceStatusSchema = z.enum(DEVICE_STATUSES);
/**
 * The wire-level identifier for which kiosk app a device is paired as.
 *
 * This is NOT the authoritative grant list — `packages/authz` `DEVICE_ROLES`
 * (`roles.ts`) is, and `can()` resolves a device's permissions from there,
 * keyed by this same string. `contracts` cannot import `authz` (dependency
 * boundary, CLAUDE.md), so the two lists are kept in sync by a test in
 * `packages/authz` that asserts `Object.keys(DEVICE_ROLES)` equals this tuple,
 * not by a shared import.
 */
export const DEVICE_APP_IDS = ['visitor-kiosk', 'mailroom-kiosk'];
export const deviceAppIdSchema = z.enum(DEVICE_APP_IDS);
/**
 * The wire shape of a paired device. An allow-list, deliberately narrower
 * than the row: `tenantId` and `deviceTokenHash` never leave the server
 * (docs/008 S1/S7; docs/adr/0008 — the token is opaque and hashed at rest,
 * so there is nothing here to leak even by accident).
 *
 * `id`/`locationId` are `cuid(2)` strings, like every other id in this
 * package (see `person.ts`/`location.ts`) — not RFC-4122 UUIDs, so they are
 * validated with `.min(1)`, not `.uuid()`.
 */
export const deviceDTOSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    locationId: z.string().min(1),
    locationName: z.string().nullable(),
    /**
     * The printer this kiosk prints to, or `null` to use its location's default (PRT-01b).
     * Mirrors `Device.badgePrinterId` (packages/db/prisma/schema.prisma) — nullable, `onDelete:
     * SetNull` — so a printer being deleted or a device never having been mapped are the SAME
     * observable state here, exactly as they are in the database. REQUIRED KEY, NULLABLE VALUE:
     * same shape as `locationName` / `lastSeenAt` / `appVersion` immediately around it — an
     * absent key is never valid, only `null` is.
     */
    badgePrinterId: z.string().min(1).nullable(),
    appId: deviceAppIdSchema,
    status: deviceStatusSchema,
    lastSeenAt: z.string().datetime().nullable(),
    appVersion: z.string().nullable(),
    createdAt: z.string().datetime(),
});
/** `POST /api/web/devices/pairing-codes` — an admin minting a code for a kiosk to redeem. */
export const createPairingCodeSchema = z.object({
    locationId: z.string().min(1),
    name: label({ max: 80 }),
});
/**
 * What the admin's create call returns.
 *
 * `code` is returned exactly once, at creation time, in this response body.
 * The server stores only a hash of it (same shape as the device token,
 * docs/adr/0008) and it is never readable again — not through this DTO, not
 * through any other endpoint. Losing it means minting a new code.
 */
export const pairingCodeDTOSchema = z.object({
    code: z.string().regex(/^\d{6}$/),
    name: z.string().min(1),
    locationId: z.string().min(1),
    expiresAt: z.string().datetime(),
});
/** `POST /api/device/v1/devices/pair` — the one `publicRoute()` on the device surface. */
export const pairDeviceSchema = z.object({
    code: z.string().regex(/^\d{6}$/),
    appVersion: z.string().trim().max(32).optional(),
});
/**
 * What a successful pairing returns.
 *
 * `deviceToken` is shown to the kiosk exactly once, here, and stored by the
 * server only as a hash (`docs/adr/0008`) — there is no endpoint that can
 * ever show it again. The kiosk is responsible for persisting it (in
 * `localStorage`, per the S4-M1 plan) the moment it receives this response.
 */
export const pairedDeviceDTOSchema = z.object({
    deviceToken: z.string().min(1),
    device: deviceDTOSchema,
});
/** `GET /api/device/v1/devices/me` — what a paired kiosk sees about itself. */
export const deviceSessionDTOSchema = z.object({
    deviceId: z.string().min(1),
    /**
     * The paired device's own tenant (docs/adr/0032): the kiosk tags its Crashlytics
     * reports with it so a crash is attributable per tenant. READ-ONLY on the device —
     * no device route accepts a `tenantId`; every one derives it from the verified
     * device token (docs/008 S1). An opaque `cuid(2)`, like `deviceId`/`locationId`.
     * Unlike `deviceDTOSchema` (the admin-facing device row), the session is only ever
     * returned to the device itself.
     */
    tenantId: z.string().min(1),
    name: z.string().min(1),
    appId: deviceAppIdSchema,
    status: deviceStatusSchema,
    locationId: z.string().min(1),
    locationName: z.string().nullable(),
    tenantName: z.string().min(1),
    /**
     * The tenant's `kioskAnalyticsEnabled` privacy switch (docs/adr/0032 Slice 3). The kiosk
     * turns Firebase Analytics collection on only while this is `true`, re-reading it on every
     * session load and heartbeat, so a change in Settings → Privacy reaches a paired kiosk
     * within one heartbeat. ADDITIVE AND REQUIRED (same reasoning as `branding` below); the
     * kiosk parses it as optional-defaulting-to-false so it still reads an older API.
     * Crash reporting is not governed by it.
     */
    kioskAnalyticsEnabled: z.boolean(),
    /**
     * The kiosk appearance configured at this device's location (S23).
     *
     * ADDITIVE AND REQUIRED. Additive, so it ships in place on `/api/device/v1`
     * with no version bump (docs/002 "API surfaces"): an older kiosk build reads
     * named keys and simply ignores it. Required rather than optional, so the
     * server can never accidentally omit it and leave a client guessing whether
     * "absent" means unbranded or means an older server.
     *
     * BOTH FIELDS NULL = UNBRANDED, and the kiosk keeps its own default teal. The
     * device never computes a foreground: `accentForeground` is derived
     * server-side by `onAccentForeground` so there is exactly one implementation
     * of the readability rule (milestone Decisions #4).
     *
     * `hasLogo` is a BOOLEAN, not a file id and not a URL. The id is an internal key
     * (docs/008 S7) and a URL would be a capability in a query string, which
     * `storage/port.ts` refuses. It exists so an unbranded kiosk — the common case —
     * never issues a byte request that can only 404: the bytes come from
     * `GET /api/device/v1/branding/logo` under the same device token, and only when this
     * is `true`.
     *
     * Extended inline here rather than on `kioskBrandingColorsSchema` itself: that schema
     * is shared with `kioskBrandingValuesSchema`, which grows its own `hasLogo` in
     * S23-TASK-013, and one shared field edited by two tasks is how a merge conflict
     * becomes a silent contract change.
     *
     * S36 change 6a merged in the location's welcome copy, in its UNBOUNDED outbound reading.
     * `null` on either keeps the kiosk's own `app_*.arb` default — also what an inheriting
     * location (no branding row) answers: the organization brand carries no copy.
     */
    branding: kioskBrandingColorsSchema.merge(kioskWelcomeCopySchema).extend({ hasLogo: z.boolean() }),
    /**
     * The kiosk BEHAVIOUR settings configured at this device's location (follow-up to S31,
     * docs/plans/kiosk-self-check-out.md "Follow-up slice: `Show check-out on the kiosk`
     * setting"). ADDITIVE AND REQUIRED, same reasoning as `branding` immediately above: an
     * older kiosk build reads named keys and ignores an unknown sibling, but the server must
     * never be able to omit this and leave a client guessing.
     *
     * Served by both `GET /devices/me` and `POST /devices/heartbeat`, so a change reaches a
     * paired kiosk within one heartbeat (60s) with no re-pairing. A missing `KioskSetting`
     * row resolves to `selfCheckOutEnabled: true` server-side before this DTO is built — the
     * kiosk never has to know "absent" from "explicitly true".
     *
     * `hostLookupMode`, `hostLookupMinChars` and `checkOutMatchMode` are additive (S43-yjbf):
     * an older kiosk ignores them, and a newer kiosk talking to an older API must default
     * them itself (S43-yjbf-TASK-006 does). A missing row resolves to `SUGGEST` / `3` /
     * `PARTIAL` server-side.
     */
    kioskSettings: z.object({
        selfCheckOutEnabled: z.boolean(),
        hostLookupMode: hostLookupModeSchema,
        hostLookupMinChars: hostLookupMinCharsSchema,
        checkOutMatchMode: checkOutMatchModeSchema,
    }),
    /**
     * Whether the kiosk welcome screen shows "Powered by InstaCheckin". The server computes it as
     * NOT the tenant's `visitor.remove_powered_by` entitlement (off on every plan; an operator
     * override turns it on for one tenant), on both `GET /devices/me` and `POST /devices/heartbeat`.
     *
     * ADDITIVE AND OPTIONAL, and ABSENT MEANS SHOWN: the current server always sends it, but a newer
     * kiosk talking to an older API must keep the attribution — the safe default is the one that
     * matches every plan. Reading an entitlement to SHAPE device config is allowed on
     * `/api/device/v1`; refusing on one is not (`lib/require-entitlement.ts`).
     */
    showPoweredBy: z.boolean().optional(),
});
/**
 * `GET /api/web/devices` — pagination plus the optional location filter.
 *
 * `locationId` is a CLIENT REQUEST, never an authorization decision: the route
 * intersects it with `allowedLocationIds(auth, 'visitor.device.manage')` through
 * `resolveLocationScope` before it reaches any `WHERE` (S19-TASK-007). Same shape and
 * same reasoning as `inviteFilterQuerySchema.locationId`.
 */
export const deviceListQuerySchema = pageQuerySchema.merge(z.object({
    locationId: z.string().min(1).optional(),
}));
//# sourceMappingURL=device.js.map